import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./ClassesTable.css";
import { ReactComponent as Undo } from "../../../../icons/undo.svg";
import ClassesFilter from "./ClassesFilter";
import ClassWiseModal from "./ClassWiseModal";

// ─── Types ───────────────────────────────────────────────
interface ClassSlotData {
  quantum: number;
  rate: number;
  bm: number;
  sm: number;
  sam: number;
}

interface SlotRow {
  slotIndex: number; // 1-based
  timeRange: string;
  demandMw: number;
  classes: ClassSlotData[]; // one per class
}

// 96 slots × 15 min
const generateTimeRanges = (): string[] => {
  const ranges: string[] = [];
  for (let s = 0; s < 96; s++) {
    const startMin = s * 15;
    const endMin = startMin + 15;
    const fmt = (m: number) => {
      const h = Math.floor(m / 60) % 24;
      const mm = m % 60;
      return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    };
    ranges.push(`${fmt(startMin)} - ${fmt(endMin)}`);
  }
  return ranges;
};

const TIME_RANGES = generateTimeRanges();

const CLASS_FIELDS = ["quantum", "rate", "bm", "sm", "sam"] as const;
type ClassField = (typeof CLASS_FIELDS)[number];

const FIELD_LABELS: Record<ClassField, string> = {
  quantum: "Quantum",
  rate: "Rate",
  bm: "BM",
  sm: "SM",
  sam: "SAM",
};

type BlockSelection =
  | number
  | { value?: number | string; label?: number | string };

const resolveBlockNumber = (block: BlockSelection): number | null => {
  if (typeof block === "number") return Number.isFinite(block) ? block : null;
  if (block && typeof block === "object") {
    const parsed = Number(block.value ?? block.label);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

// ─── API data → SlotRow[] transform ─────────────────────
interface ApiGenerator {
  generator: string;
  vc: number;
  available: number;
}

interface ApiClassRow {
  time_slot__slot: number;
  time_string: string;
  generator_class: number;
  generators: ApiGenerator[];
  generator_count: number;
  demand_mw: number;
  quantum: number;
  total_capacity: number;
  must_run: number;
  must_run_mw: number;
  vc: number;
  bm: number;
  sm: number;
  sam: number;
}

function transformApiData(apiData: ApiClassRow[]): {
  rows: SlotRow[];
  numClasses: number;
  sortedClasses: number[];
} {
  if (!apiData || apiData.length === 0)
    return { rows: [], numClasses: 0, sortedClasses: [] };

  // Find distinct classes
  const classSet = new Set(apiData.map((r) => r.generator_class));
  const sortedClasses = Array.from(classSet).sort((a, b) => a - b);
  const classIndexMap = new Map(sortedClasses.map((c, i) => [c, i]));
  const numClasses = sortedClasses.length;

  // Group by slot
  const slotMap = new Map<
    number,
    { timeString: string; demandMw: number; classes: ClassSlotData[] }
  >();

  for (const row of apiData) {
    if (!slotMap.has(row.time_slot__slot)) {
      slotMap.set(row.time_slot__slot, {
        timeString: row.time_string,
        demandMw: row.demand_mw,
        classes: Array.from({ length: numClasses }, () => ({
          quantum: 0,
          rate: 0,
          bm: 0,
          sm: 0,
          sam: 0,
        })),
      });
    }
    const entry = slotMap.get(row.time_slot__slot)!;
    const ci = classIndexMap.get(row.generator_class)!;
    entry.classes[ci] = {
      quantum: row.quantum,
      rate: row.vc,
      bm: row.bm,
      sm: row.sm,
      sam: row.sam,
    };
  }

  const rows: SlotRow[] = Array.from(slotMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([slot, entry]) => ({
      slotIndex: slot,
      timeRange: entry.timeString,
      demandMw: entry.demandMw,
      classes: entry.classes,
    }));

  return { rows, numClasses, sortedClasses };
}

// ─── Props ───────────────────────────────────────────────
interface ClassesTableProps {
  maxHeight?: string;
  mode?: "normal" | "edit";
  apiData?: ApiClassRow[];
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit?: (data: any) => void;
  loading?: boolean;
  selectedBlock?: BlockSelection[];
  modalFilter?: boolean;
  setModalFilter?: React.Dispatch<React.SetStateAction<boolean>>;
  rawApiData?: ApiClassRow[]; // full unfiltered data for class-wise modal
}

interface EditingCell {
  slot: number;
  classIdx: number;
  field: ClassField;
}

function SortIcon({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  return (
    <span
      className={`text-[10px] leading-none ${active ? "text-[#34656D]" : "text-gray-300"}`}
    >
      {active && dir === "desc" ? "▼" : "▲"}
    </span>
  );
}

// ─── Component ───────────────────────────────────────────
export function ClassesTable({
  maxHeight = "calc(-300px + 100vh)",
  mode = "normal",
  apiData,
  setModalOpen,
  handleSubmit,
  loading = false,
  selectedBlock = [],
  modalFilter = false,
  setModalFilter,
  rawApiData,
}: ClassesTableProps) {
  const {
    rows: allRows,
    numClasses,
    sortedClasses,
  } = useMemo(() => transformApiData(apiData ?? []), [apiData]);

  // Class-wise detail modal state
  const [classWiseOpen, setClassWiseOpen] = useState(false);
  const [classWiseNumber, setClassWiseNumber] = useState(1);

  const openClassWise = useCallback(
    (classNum: number) => {
      // Only open modal in normal (read) mode, not in Modification Mode
      if (mode === "edit") return;
      setClassWiseNumber(classNum);
      setClassWiseOpen(true);
    },
    [mode],
  );

  // VC range per class: chained so class N min = class (N-1) max
  const vcRangeMap = useMemo(() => {
    const source = rawApiData ?? apiData ?? [];
    const perClassMax = new Map<number, number>();
    for (const row of source) {
      for (const gen of row.generators ?? []) {
        const vc = gen.vc;
        if (vc == null || !Number.isFinite(vc)) continue;
        const existing = perClassMax.get(row.generator_class);
        if (existing === undefined || vc > existing)
          perClassMax.set(row.generator_class, vc);
      }
    }
    const sorted = [...perClassMax.entries()].sort((a, b) => a[0] - b[0]);
    const map = new Map<number, { min: number; max: number }>();
    sorted.forEach(([cls, max], idx) => {
      const min = idx === 0 ? 0 : sorted[idx - 1][1];
      map.set(cls, { min, max });
    });
    return map;
  }, [rawApiData, apiData]);

  // Filter by selected blocks
  const visibleSlots = useMemo(() => {
    if (!Array.isArray(selectedBlock) || selectedBlock.length === 0) {
      return allRows;
    }
    const selected = selectedBlock
      .map(resolveBlockNumber)
      .filter((s): s is number => s !== null && s >= 1 && s <= 96);
    if (selected.length === 0) return allRows;
    const set = new Set(selected);
    return allRows.filter((r) => set.has(r.slotIndex));
  }, [allRows, selectedBlock]);

  // Sort state
  type SortKey =
    | { kind: "slot" }
    | { kind: "field"; classIdx: number; field: ClassField };
  type SortState = { key: SortKey; dir: "asc" | "desc" } | null;
  const [sortState, setSortState] = useState<SortState>(null);

  const sortKey = sortState?.key ?? null;
  const sortDir = sortState?.dir ?? "asc";

  const toggleSort = useCallback((key: SortKey) => {
    setSortState((prev) => {
      const same =
        prev !== null && JSON.stringify(prev.key) === JSON.stringify(key);
      if (same) {
        return { key, dir: prev!.dir === "asc" ? "desc" : "asc" };
      }
      // First click on a new column → default to descending so the change is
      // visible even when the data is already in natural ascending order.
      return { key, dir: "desc" };
    });
  }, []);

  const rows = useMemo(() => {
    if (!sortState) return visibleSlots;
    const { key, dir } = sortState;
    return [...visibleSlots].sort((a, b) => {
      let av: number, bv: number;
      if (key.kind === "slot") {
        av = a.slotIndex;
        bv = b.slotIndex;
      } else if (key.kind === "field" && (key as any).classIdx === -1) {
        // demand sort
        av = a.demandMw;
        bv = b.demandMw;
      } else {
        av = a.classes[key.classIdx]?.[key.field] ?? 0;
        bv = b.classes[key.classIdx]?.[key.field] ?? 0;
      }
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [visibleSlots, sortState]);

  const totalSlots = rows.length;

  // Edit state
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [localValues, setLocalValues] = useState<Record<string, any>>({});
  const [draftValue, setDraftValue] = useState("");

  const isEditMode = mode === "edit";

  const valueKey = useCallback(
    (slot: number, classIdx: number, field: string) =>
      `${slot}__${classIdx}__${field}`,
    [],
  );

  const getOriginal = useCallback(
    (slotIdx: number, classIdx: number, field: ClassField) => {
      const row = allRows.find((r) => r.slotIndex === slotIdx);
      return row?.classes[classIdx]?.[field] ?? 0;
    },
    [allRows],
  );

  const startEdit = (slot: number, classIdx: number, field: ClassField) => {
    if (!isEditMode) return;
    if (field === "quantum") return;
    if (
      editingCell &&
      editingCell.slot === slot &&
      editingCell.classIdx === classIdx &&
      editingCell.field === field
    )
      return;
    const k = valueKey(slot, classIdx, field);
    const original = getOriginal(slot, classIdx, field);
    const currentValue =
      localValues[k] !== undefined ? localValues[k] : original;

    let initialDraft = currentValue == null ? "" : String(currentValue);
    if (field === "rate" && typeof currentValue === "number") {
      initialDraft = currentValue.toFixed(3);
    }

    setDraftValue(initialDraft);
    setEditingCell({ slot, classIdx, field });
  };

  const confirmEdit = () => {
    if (!editingCell) return;
    const { slot, classIdx, field } = editingCell;
    const k = valueKey(slot, classIdx, field);
    const trimmed = draftValue.trim();
    let valueToSave: string | number =
      trimmed === "" ? "" : isNaN(Number(trimmed)) ? trimmed : Number(trimmed);

    if (typeof valueToSave === "number" && field === "rate") {
      valueToSave = Number(valueToSave.toFixed(3));
    }

    const original = getOriginal(slot, classIdx, field);
    const prev = Object.prototype.hasOwnProperty.call(localValues, k)
      ? localValues[k]
      : original;
    if (valueToSave === prev) {
      setEditingCell(null);
      setDraftValue("");
      return;
    }
    setLocalValues((p) => ({ ...p, [k]: valueToSave }));
    setEditingCell(null);
    setDraftValue("");
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setDraftValue("");
  };

  const revertCell = (slot: number, classIdx: number, field: string) => {
    const k = valueKey(slot, classIdx, field);
    setLocalValues((p) => {
      if (!(k in p)) return p;
      const { [k]: _, ...rest } = p;
      return rest;
    });
  };

  const revertAll = () => {
    if (!Object.keys(localValues).length) return;
    setLocalValues({});
    cancelEdit();
  };

  const handleUpdateClick = () => {
    const changedEntries = Object.entries(localValues);
    if (!changedEntries.length) return;

    // Field name mapping: internal → API
    const fieldToApi: Record<string, string> = {
      quantum: "quantum",
      rate: "vc",
      bm: "bm",
      sm: "sm",
      sam: "sam",
    };

    // Group changes by (slot, classIdx) → merged fields
    const grouped = new Map<string, Record<string, any>>();
    for (const [key, value] of changedEntries) {
      const [slotStr, classIdxStr, field] = key.split("__");
      const slot = Number(slotStr);
      const classIdx = Number(classIdxStr);
      const groupKey = `${slot}__${classIdx}`;
      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, {
          time_slot__slot: slot,
          generator_class: sortedClasses[classIdx],
        });
      }
      const apiField = fieldToApi[field] ?? field;
      grouped.get(groupKey)![apiField] = value;
    }

    handleSubmit?.({ data: Array.from(grouped.values()) });
  };

  const applyBulkChanges = useCallback(
    (
      changes: Array<{
        slotIndex: number;
        classIdx: number;
        field: string;
        value: number;
      }>,
    ) => {
      if (!changes.length) return;
      setLocalValues((prev) => {
        const next = { ...prev };
        for (const { slotIndex, classIdx, field, value } of changes) {
          next[valueKey(slotIndex, classIdx, field)] = value;
        }
        return next;
      });
    },
    [valueKey],
  );

  const uniqueSlots = useMemo(() => allRows.map((r) => r.slotIndex), [allRows]);

  useEffect(() => {
    setEditingCell(null);
  }, [mode]);

  useEffect(() => {
    setLocalValues({});
    setEditingCell(null);
  }, [apiData]);

  const localSize = Object.keys(localValues).length;

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
        No data available
      </div>
    );
  }

  return (
    <div className="relative flex flex-col rounded-xl border-none h-full">
      <div
        className={`overflow-auto flex-1 min-h-0 border-none classes_scroll_container ${isEditMode ? "rounded-t-xl" : "rounded-xl"}`}
        style={{ maxHeight }}
      >
        <table id="classesTable" className="classes_table">
          <thead className="sticky top-0 z-50">
            {/* Row 1: Empty corner + Demand + Class group headers */}
            <tr>
              <th className="cls_th_corner" />
              <th className="cls_th_frozen_demand" />
              {Array.from({ length: numClasses }, (_, ci) => {
                const cls = sortedClasses[ci];
                const range = vcRangeMap.get(cls);
                const vcLabel = range
                  ? `VC: ${range.min.toFixed(2)} – ${range.max.toFixed(2)}`
                  : null;
                const isClickable = mode !== "edit";
                return (
                  <th
                    key={ci}
                    colSpan={CLASS_FIELDS.length}
                    className={`cls_th_class_group relative ${ci === numClasses - 1 ? "cls_th_class_group_last" : ""} ${isClickable ? "cursor-pointer select-none group" : ""}`}
                    onClick={() => openClassWise(cls)}
                    title={
                      isClickable
                        ? `View generator details for Class ${cls}`
                        : undefined
                    }
                  >
                    <div className="flex items-center justify-center leading-tight">
                      <span className="inline-flex items-center gap-1.5">
                        <span>Class {cls}</span>
                        {vcLabel && (
                          <span
                            className="text-[10px] font-normal"
                            style={{
                              color: "#5a8c95",
                              letterSpacing: "0.01em",
                            }}
                          >
                            ({vcLabel})
                          </span>
                        )}
                      </span>
                    </div>
                    {isClickable && (
                      <span
                        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-[9px] px-1.5 py-0.5 rounded-full"
                        style={{
                          background: "rgba(52,101,109,0.15)",
                          color: "#34656D",
                        }}
                      >
                        View ↗
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>

            {/* Row 2: "Time Slots" frozen + Demand + sub-column headers */}
            <tr>
              <th
                className="cls_th_frozen_slot cursor-pointer select-none"
                onClick={() => toggleSort({ kind: "slot" })}
              >
                <span className="inline-flex items-center gap-1">
                  Time Slots{" "}
                  <SortIcon active={sortKey?.kind === "slot"} dir={sortDir} />
                </span>
              </th>
              <th
                className="cls_th_sub_frozen_demand cursor-pointer select-none"
                onClick={() =>
                  toggleSort({
                    kind: "field",
                    classIdx: -1,
                    field: "demand" as any,
                  })
                }
              >
                <span className="inline-flex items-center gap-1">
                  Demand (MW){" "}
                  <SortIcon
                    active={
                      sortKey?.kind === "field" &&
                      (sortKey as any).classIdx === -1
                    }
                    dir={sortDir}
                  />
                </span>
              </th>
              {Array.from({ length: numClasses }, (_, ci) =>
                CLASS_FIELDS.map((field, fi) => {
                  const isActive =
                    sortKey?.kind === "field" &&
                    (sortKey as any).classIdx === ci &&
                    (sortKey as any).field === field;
                  const isLastField = fi === CLASS_FIELDS.length - 1;
                  const isLastClass = ci === numClasses - 1;
                  return (
                    <th
                      key={`${ci}-${field}`}
                      className={`cls_th_sub cursor-pointer select-none ${isLastField && !isLastClass ? "cls_th_sub_group_end" : ""} ${isLastField && isLastClass ? "cls_th_sub_last" : ""}`}
                      onClick={() =>
                        toggleSort({ kind: "field", classIdx: ci, field })
                      }
                    >
                      <span className="inline-flex items-center gap-1">
                        {FIELD_LABELS[field]}
                        <SortIcon active={isActive} dir={sortDir} />
                      </span>
                    </th>
                  );
                }),
              )}
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr key={row.slotIndex} className="cls_tr_body">
                {/* Frozen: Time Slot */}
                <td className="cls_td_frozen_slot">
                  Slot {row.slotIndex}: ({row.timeRange})
                </td>

                {/* Demand */}
                <td className="cls_td_frozen_demand">{row.demandMw}</td>

                {/* Class data cells */}
                {row.classes.map((cls, ci) =>
                  CLASS_FIELDS.map((field, fi) => {
                    const k = valueKey(row.slotIndex, ci, field);
                    const original = cls[field];
                    const rawDisplay =
                      localValues[k] !== undefined ? localValues[k] : original;
                    const display =
                      typeof rawDisplay === "number"
                        ? field === "quantum"
                          ? rawDisplay.toFixed(2)
                          : field === "rate"
                            ? rawDisplay.toFixed(3)
                            : rawDisplay
                        : rawDisplay;
                    const differs =
                      Object.prototype.hasOwnProperty.call(localValues, k) &&
                      localValues[k] !== original;
                    const isEditing =
                      editingCell &&
                      editingCell.slot === row.slotIndex &&
                      editingCell.classIdx === ci &&
                      editingCell.field === field;
                    const isLastField = fi === CLASS_FIELDS.length - 1;
                    const isLastClass = ci === numClasses - 1;
                    const classBg =
                      ci % 2 === 0 ? "cls_class_even" : "cls_class_odd";
                    const groupEnd =
                      isLastField && !isLastClass ? "cls_td_group_end" : "";

                    const canEdit = isEditMode && field !== "quantum";
                    return (
                      <td
                        key={`${ci}-${field}`}
                        className={`cls_td ${classBg} ${groupEnd} ${canEdit ? "cursor-pointer" : ""} relative`}
                        onClick={() => {
                          if (canEdit && !isEditing)
                            startEdit(row.slotIndex, ci, field);
                        }}
                      >
                        {differs && !isEditing && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              revertCell(row.slotIndex, ci, field);
                            }}
                            className="absolute top-0 right-0 bg-amber-500 hover:bg-amber-600 text-white rounded-bl text-[10px] px-1.5 py-[1px] leading-tight shadow z-[9]"
                            title="Revert"
                          >
                            ↺
                          </button>
                        )}

                        {!isEditing && (
                          <span
                            className={
                              differs ? "text-[#F1935C] font-semibold" : ""
                            }
                          >
                            {display}
                          </span>
                        )}

                        {isEditing && (
                          <div className="absolute z-[9999] inset-0 flex items-center justify-center">
                            <div className="flex border border-gray-300 shadow-lg bg-white rounded">
                              <input
                                autoFocus
                                step={
                                  editingCell?.field === "rate"
                                    ? "0.001"
                                    : "any"
                                }
                                inputMode="decimal"
                                type="number"
                                className="w-20 h-8 bg-[#f8f7f6] font-medium text-right pr-2 pl-2 py-1 focus:outline-none border-0 rounded-l"
                                value={draftValue}
                                onChange={(e) => setDraftValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") confirmEdit();
                                  else if (e.key === "Escape") cancelEdit();
                                }}
                                onBlur={(e) => {
                                  if (
                                    editingCell &&
                                    !e.currentTarget.parentElement?.contains(
                                      e.relatedTarget as Node,
                                    )
                                  ) {
                                    cancelEdit();
                                  }
                                }}
                              />
                              <button
                                type="button"
                                tabIndex={-1}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmEdit();
                                }}
                                className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white text-sm flex items-center justify-center border-0 rounded-r"
                                title="Save"
                              >
                                ✓
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    );
                  }),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit instructions (edit mode only) */}
      {isEditMode && (
        <div className="px-4 py-2 border-t border-[#DFEAF2] text-[11px] text-[#9B98AF] leading-relaxed bg-[#FAFAFA]">
          <span className="font-semibold text-[#525062]">How to edit: </span>
          Click any cell → type new value → press{" "}
          <kbd className="px-1 py-0.5 rounded bg-gray-100 border border-gray-300 font-mono text-[10px]">
            Enter
          </kbd>{" "}
          or <span className="text-green-600 font-medium">✓</span> to confirm,{" "}
          <kbd className="px-1 py-0.5 rounded bg-gray-100 border border-gray-300 font-mono text-[10px]">
            Esc
          </kbd>{" "}
          to cancel. &nbsp;|&nbsp; Click{" "}
          <span className="text-amber-500 font-medium">↺</span> on a cell to
          revert that cell. &nbsp;|&nbsp; Use{" "}
          <span className="font-medium text-[#34656D]">Batch Customize</span>{" "}
          for bulk edits across rows. &nbsp;|&nbsp; Changed values show in{" "}
          <span className="text-[#F1935C] font-semibold">orange</span>. Click{" "}
          <span className="font-medium text-[#34656D]">Update & Review</span> to
          submit.
        </div>
      )}

      {/* Bottom action bar (edit mode only) */}
      {isEditMode && (
        <div className="mod-footer-bar flex justify-between items-center">
          <div className="mod-footer-metrics">
            <div className="mod-footer-metric-box">
              <span className="mod-footer-metric-label">Total Slots :</span>
              <span className="mod-footer-metric-value">{totalSlots}</span>
            </div>
            <div className="mod-footer-divider" aria-hidden="true" />
            <div className="mod-footer-metric-box">
              <span className="mod-footer-metric-label">Modifications :</span>
              <span className="mod-footer-metric-value mod-footer-metric-value--accent">
                +{localSize}
              </span>
            </div>
          </div>

          <div className="mod-footer-actions">
            <button
              onClick={() => setModalOpen(false)}
              className="mod-footer-cancel"
            >
              Cancel
            </button>
            <button
              onClick={revertAll}
              disabled={localSize === 0 || loading}
              className="mod-footer-revert"
            >
              <Undo className="w-4 h-4" />
              Revert
            </button>
            <button
              onClick={handleUpdateClick}
              className="mod-footer-update"
              disabled={localSize === 0 || loading}
            >
              {loading ? "Saving..." : "Update & Review"}
            </button>
          </div>
        </div>
      )}

      <ClassesFilter
        isOpen={modalFilter}
        onClose={() => setModalFilter?.(false)}
        uniqueBlocks={uniqueSlots}
        numClasses={numClasses}
        loading={loading}
        onLocalApply={applyBulkChanges}
      />

      {/* Class-wise generator detail modal */}
      <ClassWiseModal
        isOpen={classWiseOpen}
        onClose={() => setClassWiseOpen(false)}
        classNumber={classWiseNumber}
        apiData={rawApiData ?? apiData ?? []}
      />
    </div>
  );
}
